import Button from '@/common/components/Button'
import { EmptyDataMessage } from '@/common/components/EmptyDataMessage'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import Modal from '@/common/components/Modal'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import { TicketData } from '@/common/types/support/ticket.type'
import { cn } from '@/common/utils/cn'
import { supportTicketTypeStrategy } from '@/features/support/internal/constants/supportTicketTypeOptions.constant'
import { useSupportAvailableTickets } from '@/features/support/internal/hooks/useSupportAvailableTickets'
import { PlusIcon, SearchIcon, TrashIcon } from 'lucide-react'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { Tooltip } from 'react-tooltip'

interface ModalAddSupportTicketsProps {
  supportTickets: TicketData[]
  setSupportTickets: Dispatch<SetStateAction<TicketData[]>>
  hideTicketIds?: string[]
}

export const ModalAddSupportTickets = ({
  supportTickets,
  setSupportTickets,
  hideTicketIds,
}: ModalAddSupportTicketsProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [tempSupportTickets, setTempSupportTickets] = useState<TicketData[]>(supportTickets)

  const [searchSupportTickets, setSearchSupportTickets, _, forceNewSearchSupportTicketsValue] = useDebounce('')

  const {
    data: availableTickets,
    meta,
    isFetching: isFetchingAvailableTickets,
    fetchNextPage,
    error: availableTicketsError = {} as any,
    refetch: refetchAvailableTickets
  } = useSupportAvailableTickets<TicketData[]>({
    filters: { search: searchSupportTickets },
  })

  const isToFetchNextPage = () => {
    if (!containerRef.current || meta.current_page >= meta.total_pages || isFetchingAvailableTickets) {
      return false
    }

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current
    return scrollTop + clientHeight >= scrollHeight - 16
  }

  const handleScroll = () => {
    const haveNextPage = isToFetchNextPage()

    if (haveNextPage) {
      fetchNextPage()
    }
  }

  useEffect(() => {
    if (isOpen) {
      setTempSupportTickets(supportTickets)
      forceNewSearchSupportTicketsValue('')
    }
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      Trigger={
        <Button
          type="button"
          name='add_linked_support_ticket'
        >
          Adicionar chamado
        </Button>
      }
      title="Vincular chamado"
      titleClass="text-2xl text-white-800"
      className="max-w-3xl bg-black/50 backdrop-blur-xl"
      bodyClassName="text-white-800 bg-white-100"
      body={
        <ErrorMessageWrapper
          className="max-h-[calc(100vh-256px)]"
          statusCode={availableTicketsError?.status}
          refetchFunction={refetchAvailableTickets}
        >
          <div className="flex w-full flex-col gap-y-4 max-h-[calc(100vh-16rem)]">
            <TextField
              name='support_ticket_search'
              LeftIcon={<SearchIcon className="-translate-y-1 translate-x-1" size="18px" />}
              label="Insira o código do chamado, nome do autor ou nome da empresa"
              placeholder="Pesquisar"
              onChange={(event) => setSearchSupportTickets((event.target as HTMLInputElement).value)}
              className="text-xs"
            />

            {availableTickets?.filter((ticket) => !hideTicketIds?.includes(ticket.id)).length ? (
              <>
                <div
                  ref={containerRef}
                  onScroll={handleScroll}
                  className="flex w-full flex-col gap-y-4 overflow-y-scroll thin-scroll pr-4"
                >
                  {availableTickets
                    ?.filter((ticket) => !hideTicketIds?.includes(ticket.id))
                    .map((supportTicket) => {
                      const alreadyAdded = tempSupportTickets.find((u) => u.id === supportTicket.id)

                      return (
                        <button
                          data-testid='select_deselect_linked_support_ticket_button'
                          key={supportTicket.id}
                          onClick={() => {
                            setTempSupportTickets((prevTickets) => {
                              if (!!alreadyAdded) {
                                return prevTickets.filter((t) => t.id !== supportTicket.id)
                              }
                              return [...prevTickets, supportTicket]
                            })
                          }}
                          className={cn(
                            'flex w-full text-start gap-x-2 bg-white-100 border border-transparent rounded-lg p-4',
                            {
                              'border-blue-700': alreadyAdded,
                            },
                          )}
                        >
                          <div className="flex w-full flex-col">
                            <p className="text-sm font-light italic">Código</p>

                            <p className="w-full max-w-[7.375rem] text-lg font-bold text-nowrap text-ellipsis overflow-hidden">
                              {supportTicket.reference_number || '--'}
                            </p>
                          </div>

                          <div
                            data-tooltip-id={
                              supportTicket.branch?.company?.general_information?.fantasy_name
                                ? `supportTicket-${supportTicket.id}-company-name`
                                : undefined
                            }
                            className="flex w-full flex-col"
                          >
                            <p className="text-sm font-light italic">Nome da empresa</p>

                            <p className="w-full max-w-[7.375rem] text-lg font-bold text-nowrap text-ellipsis overflow-hidden">
                              {supportTicket.branch?.company?.general_information?.fantasy_name || '--'}
                            </p>

                            <Tooltip
                              id={`supportTicket-${supportTicket.id}-company-name`}
                              content={supportTicket.branch?.company?.general_information?.fantasy_name || '--'}
                            />
                          </div>

                          <div
                            data-tooltip-id={`supportTicket-${supportTicket.id}-creator`}
                            className="flex w-full flex-col"
                          >
                            <p className="text-sm font-light italic">Autor</p>

                            <p className="w-full max-w-[7.375rem] text-lg font-bold text-nowrap text-ellipsis overflow-hidden">
                              {supportTicket.creator?.full_name || '--'}
                            </p>

                            <Tooltip
                              id={`supportTicket-${supportTicket.id}-creator`}
                              content={supportTicket.creator?.full_name || '--'}
                            />
                          </div>

                          <div className="flex w-full flex-col">
                            <p className="text-sm font-light italic">Tipo de chamado</p>

                            <p className="w-full max-w-[7.375rem] text-lg font-bold text-nowrap text-ellipsis overflow-hidden">
                              {supportTicketTypeStrategy[
                                supportTicket.ticket_type as keyof typeof supportTicketTypeStrategy
                              ] || '--'}
                            </p>
                          </div>

                          <div
                            data-tooltip-id={`supportTicket-${supportTicket.id}-reason`}
                            className="flex w-full flex-col"
                          >
                            <p className="text-sm font-light italic">Motivo</p>

                            <p className="w-full max-w-[7.375rem] text-lg font-bold text-nowrap text-ellipsis overflow-hidden">
                              {supportTicket.reason?.display_name || '--'}
                            </p>

                            <Tooltip
                              id={`supportTicket-${supportTicket.id}-reason`}
                              content={supportTicket.reason?.display_name || '--'}
                            />
                          </div>

                          <div className="my-auto">
                            {!alreadyAdded ? (
                              <PlusIcon className="size-4 shrink-0" />
                            ) : (
                              <TrashIcon className="size-4 shrink-0 text-danger" />
                            )}
                          </div>
                        </button>
                      )
                    })}
                </div>

                <div className="flex w-[48rem] -m-6 mt-0">
                  <Button
                    type="button"
                    variant="neutral"
                    className="w-full bg-transparent rounded-none rounded-bl-sm py-4"
                    onClick={() => setIsOpen(false)}
                  >
                    Cancelar
                  </Button>

                  <Button
                    type="button"
                    name="confirm_linked_support_ticket"
                    className="w-full rounded-none rounded-br-sm py-4"
                    onClick={() => {
                      setSupportTickets(tempSupportTickets)
                      setIsOpen(false)
                    }}
                  >
                    Confirmar
                  </Button>
                </div>
              </>
            ) : (
              <EmptyDataMessage message="Nenhum chamado encontrado" />
            )}
          </div>
        </ErrorMessageWrapper>
      }
    />
  )
}
